<template>
    <div class="contact-distributions-by-contact-property" :class="{ fullscreen: fullScreen }">
        <div v-for="property in data" :key="property.propertyId" class="property-chart">
            <div class="toolbar">
                <div class="left">
                    <h3>{{ property.propertyLabel }}</h3>
                    <div class="buttons">
                        <IconButton
                            :classNames="tableModes[property.propertyId] ? '' : 'selected'"
                            :onClick="() => setTableMode(property.propertyId, false)"
                            v-tippy="{ content: $t('chart.chartView') }"
                        >
                            <md-icon>insights</md-icon>
                        </IconButton>
                        <IconButton
                            :classNames="tableModes[property.propertyId] ? 'selected' : ''"
                            :onClick="() => setTableMode(property.propertyId, true)"
                            v-tippy="{ content: $t('chart.tableView') }"
                        >
                            <md-icon>table_view</md-icon>
                        </IconButton>
                        <IconButton :onClick="() => exportToExcel(property)" v-tippy="{ content: $t('chart.download') }">
                            <md-icon>download</md-icon>
                        </IconButton>
                        <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('chart.fullScreen') }">
                            <md-icon v-if="!fullScreen">fullscreen</md-icon>
                            <md-icon v-else>fullscreen_exit</md-icon>
                        </IconButton>
                    </div>
                    <a-radio-group v-model="chartTypes[property.propertyId]" button-style="solid">
                        <a-radio-button value="bar">{{ $t('chart.columnChart') }}</a-radio-button>
                        <a-radio-button value="pie">{{ $t('chart.pieChart') }}</a-radio-button>
                    </a-radio-group>
                </div>
            </div>

            <div class="chart-wrapper">
                <EChartsHorizontalBar
                    v-if="!tableModes[property.propertyId] && chartTypes[property.propertyId] === 'bar'"
                    :labels="getLabels(property)"
                    :datasets="getBarDatasets(property)"
                    :height="getChartHeight(property)"
                />
                <div v-else-if="!tableModes[property.propertyId] && chartTypes[property.propertyId] === 'pie'" class="pie-chart-wrapper">
                    <EChartsDoughnut
                        :labels="getLabels(property)"
                        :datasets="getPieDatasets(property)"
                        :height="400"
                    />
                </div>
                <TableView v-else :cols="getCols()" :rows="getRows(property)" />
            </div>

            <div class="stats">
                <StatCard :title="$t('chart.mostUsed')" :value="getStats(property).maxValue" />
                <StatCard :title="$t('chart.leastUsed')" :value="getStats(property).minValue" />
                <StatCard
                    :title="$t('chart.totalPersonCount')"
                    :value="formatStat(getStats(property).total).value"
                    :unit="$t('chart.personsUnit')"
                />
            </div>
            <a-divider />
        </div>
        <a-empty v-if="!loading && (!data || data.length === 0)" />
    </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import EChartsHorizontalBar from "../../../components/charts/EChartsHorizontalBar.vue";
import EChartsDoughnut from "../../../components/charts/EChartsDoughnut.vue";
import { expertToExcel } from "../../../utils/utils";
import fscreen from "fscreen";

export default {
    name: "ContactDistributionsByContactProperty",
    components: {
        IconButton,
        StatCard,
        TableView,
        EChartsHorizontalBar,
        EChartsDoughnut
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            chartTypes: {},
            tableModes: {},
            fullScreen: false,
            cancels: [],
            loadingInstance: null
        };
    },
    methods: {
        formatStat(value) {
            if (isNaN(value) || value === null || value === undefined) {
                return {
                    value: this.$t("chart.notEnoughData"),
                    unit: "",
                };
            }
            return {
                value: value,
                unit: this.$t("chart.contactSingular")
            };
        },
        getStats(property) {
            if (!property.distributions || property.distributions.length === 0) {
                return {
                    maxValue: this.$t("chart.notEnoughData"),
                    minValue: this.$t("chart.notEnoughData"),
                    total: 0
                };
            }

            const sortedData = _.orderBy(property.distributions, ["count"], ["desc"]);
            return {
                maxValue: sortedData[0].value || this.$t("chart.notAvailable"),
                minValue: sortedData[sortedData.length - 1].value || this.$t("chart.notAvailable"),
                total: _.sumBy(sortedData, 'count')
            };
        },
        getCols() {
            return [
                {
                    title: this.$t("chart.value"),
                    dataIndex: "value",
                    key: "value",
                },
                {
                    title: this.$t("chart.contactCount"),
                    dataIndex: "count",
                    key: "count",
                }
            ];
        },
        getRows(property) {
            return property.distributions.map(item => ({
                value: item.value || this.$t("chart.notAvailable"),
                count: item.count
            }));
        },
        getLabels(property) {
            const items = property.distributions || [];
            return items.map(item => item.value || this.$t("chart.notAvailable"));
        },
        getBarDatasets(property) {
            const items = property.distributions || [];
            const palette = [
                "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6",
                "#EC4899", "#06B6D4", "#F97316", "#6366F1", "#14B8A6",
                "#84CC16", "#E11D48", "#0EA5E9", "#D946EF", "#78716C",
            ];
            return [{
                label: this.$t("chart.contactCount"),
                data: items.map(item => item.count),
                backgroundColor: items.map((_, i) => palette[i % palette.length] + "B3"),
                borderColor: items.map((_, i) => palette[i % palette.length]),
                borderWidth: 1,
                borderRadius: 4,
            }];
        },
        getPieDatasets(property) {
            const items = property.distributions || [];
            const colorPalette = [
                "rgba(59, 130, 246, 0.7)",
                "rgba(16, 185, 129, 0.7)",
                "rgba(245, 158, 11, 0.7)",
                "rgba(239, 68, 68, 0.7)",
                "rgba(139, 92, 246, 0.7)",
                "rgba(236, 72, 153, 0.7)",
                "rgba(20, 184, 166, 0.7)",
                "rgba(249, 115, 22, 0.7)",
            ];
            return [{
                data: items.map(item => item.count),
                backgroundColor: items.map((_, i) => colorPalette[i % colorPalette.length]),
                borderWidth: 2,
                borderColor: "#fff",
            }];
        },
        getChartHeight(property) {
            const items = property.distributions || [];
            return Math.max(300, items.length * 31 + 200);
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".contact-distributions-by-contact-property");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        exportToExcel(property) {
            const excelData = this.getRows(property);
            const fileName = property.propertyLabel.toLowerCase().replace(/\s+/g, "-");
            expertToExcel(excelData, `property_distributions_${fileName}_${moment().format("YYYY-MM-DD")}.xlsx`, "Sheet 1");
        },
        setTableMode(propertyId, mode) {
            this.$set(this.tableModes, propertyId, mode);
        }
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".contact-distributions-by-contact-property"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
    },
    created() {
        if (this.data) {
            this.data.forEach(property => {
                if (!this.chartTypes[property.propertyId]) {
                    this.$set(this.chartTypes, property.propertyId, 'bar');
                }
                if (this.tableModes[property.propertyId] === undefined) {
                    this.$set(this.tableModes, property.propertyId, false);
                }
            });
        }

        const fullscreenCallback = () => {
            this.fullScreen = fscreen.fullscreenElement !== null;
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach(cancel => cancel());
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
        data: {
            immediate: true,
            handler(newData) {
                if (newData) {
                    newData.forEach(property => {
                        if (!this.chartTypes[property.propertyId]) {
                            this.$set(this.chartTypes, property.propertyId, 'bar');
                        }
                        if (this.tableModes[property.propertyId] === undefined) {
                            this.$set(this.tableModes, property.propertyId, false);
                        }
                    });
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.contact-distributions-by-contact-property {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .chart-wrapper {
            height: calc(100vh - 200px);
        }
    }

    .property-chart {
        margin-bottom: 20px;

        .toolbar {
            display: flex;
            justify-content: space-between;
            margin-bottom: 16px;

            .left {
                display: flex;
                align-items: center;
                gap: 16px;

                h3 {
                    margin: 0;
                    font-size: 24px;
                }

                .buttons {
                    display: flex;
                    border: 1px solid var(--color-border-default);

                    .icon-button {
                        padding: 5px;
                        width: 40px;
                        height: 34px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        border-right: 1px solid var(--color-border-default);
                        border-radius: unset !important;

                        &.selected {
                            background-color: var(--color-bg-info-low-hover);

                            i {
                                color: rgb(55, 162, 255);
                            }
                        }

                        &:hover {
                            background-color: var(--color-bg-neutral-pressed);
                        }
                    }

                    .icon-button:last-child {
                        border-right: none;
                    }
                }
            }
        }

        .chart-wrapper {
            min-height: 300px;
            height: auto;
        }

        .pie-chart-wrapper {
            width: 100%;
            height: 400px;
        }

        .stats {
            display: flex;
            flex-direction: row;
            gap: 10px;
            flex-wrap: wrap;
            margin-top: 16px;
        }
    }
}
</style>
